import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import UrgencyBadge from './UrgencyBadge';
import CaseWorkflowProgress from '../../../components/cases/CaseWorkflowProgress';
import {
  getCaseAssistantPath,
  shouldOpenCaseViaAssistant,
} from '../../../lib/dentistSubmissionWizard';

const CaseTableRow = ({
  caseData,
  isSelected,
  onSelect,
  isAdmin = false,
  onAssignProsthetist,
  onAssignProductionStaff,
  dentistClientView = false,
  viewerRole = null,
}) => {
  const navigate = useNavigate();

const handleViewCase = () => {
  if (
    shouldOpenCaseViaAssistant(
      caseData?.id,
      caseData?.timelinePhases,
      viewerRole,
    )
  ) {
    navigate(getCaseAssistantPath(caseData.id));
    return;
  }
  navigate(`/case-detail/${caseData?.id}`);
};

const handleViewPatient = () => {
  if (!caseData?.patientId) return;
  navigate(`/patient-profile/${caseData.patientId}`);
};
  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <tr
      className="border-b border-border cursor-pointer transition-colors duration-200 hover:bg-muted/80 dark:hover:bg-muted/40"
      onClick={handleViewCase}
    >
      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
        <Checkbox
          checked={isSelected}
          onChange={(e) => onSelect(caseData?.id, e?.target?.checked)}
        />
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full overflow-hidden bg-muted flex-shrink-0">
            <Image
              src={caseData?.patientAvatar}
              alt={caseData?.patientAvatarAlt}
              className="w-full h-full object-cover"
            />
          </div>
          <div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleViewPatient();
              }}
              className="font-medium text-foreground hover:text-primary transition-colors duration-200 text-left"
            >
              {caseData?.patientName}
            </button>
            <p className="text-xs text-muted-foreground">{caseData?.caseId}</p>
          </div>
        </div>
      </td>
      {!dentistClientView && (
        <td className="px-4 py-3">
          <span className="text-sm text-foreground">
            {caseData?.dentistName || 'Non assigné'}
          </span>
        </td>
      )}
      <td className="px-4 py-3">
        <span
          className="text-sm text-foreground max-w-[14rem] line-clamp-2"
          title={caseData?.packageLabel}
        >
          {caseData?.packageLabel ?? '—'}
        </span>
      </td>
      <td className="px-4 py-3">
        <span className="text-sm text-foreground">
          {caseData?.prosthetistName || 'Non assigné'}
        </span>
      </td>
      <td className="px-4 py-3">
        {dentistClientView ? (
          <CaseWorkflowProgress
            variant="compact"
            percent={caseData?.workflowProgress?.percent ?? 0}
            activeGroupNum={caseData?.workflowProgress?.activeGroupNum ?? 1}
            totalLots={caseData?.workflowProgress?.totalLots ?? 1}
            showMacroLabel={false}
          />
        ) : (
          <span className="text-sm font-medium text-foreground">{caseData?.phase}</span>
        )}
      </td>
      <td className="px-4 py-3">
        <UrgencyBadge level={caseData?.urgency} />
      </td>
      {!dentistClientView && (
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full overflow-hidden bg-muted flex-shrink-0">
              <Image
                src={caseData?.assignedStaffAvatar}
                alt={caseData?.assignedStaffAvatarAlt}
                className="w-full h-full object-cover"
              />
            </div>
            <span className="text-sm text-foreground">{caseData?.assignedStaff}</span>
          </div>
        </td>
      )}
      <td className="px-4 py-3">
        <span className="text-sm text-muted-foreground">{formatDate(caseData?.lastActivity)}</span>
      </td>
      <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 justify-end">
          <Button
            variant="ghost"
            size="icon"
            iconName="Eye"
            onClick={handleViewCase}
          />
        
          {isAdmin && (
            <>
              <Button
                variant="ghost"
                size="icon"
                iconName="UserCog"
                title="Assigner dentiste fournisseur"
                onClick={() => onAssignProsthetist && onAssignProsthetist(caseData?.id)}
              />
              <Button
                variant="ghost"
                size="icon"
                iconName="Users"
                title="Assigner fournisseur production / distributeur"
                onClick={() => onAssignProductionStaff && onAssignProductionStaff(caseData?.id)}
              />
            </>
          )}
        </div>
      </td>
    </tr>
  );
};

export default CaseTableRow;